import { Database } from './database.types';
import { RecordType } from './types/CheckinCheckoutRecord';
import { AttendanceStatus } from "./types/Attendance";

export type Student = Database['public']['Tables']['students']['Row'];
export type TeacherBase = Database['public']['Tables']['teachers']['Row'];
export interface Teacher extends TeacherBase {
  profile_picture?: string | null;
}

export interface NameObject {
  first_name: string;
  last_name: string;
}

export interface Schedule {
  [day: string]: {
    start_time: string | null;
    end_time: string | null;
  }
}

export interface ScheduleDay {
  start?: string | null;
  end?: string | null;
  hasSchool?: boolean;
}

export interface ClassSchedule {
  monday: ScheduleDay;
  tuesday: ScheduleDay;
  wednesday: ScheduleDay;
  thursday: ScheduleDay;
  friday: ScheduleDay;
  saturday: ScheduleDay;
  sunday: ScheduleDay;
}

export interface Class extends Omit<Database['public']['Tables']['classes']['Row'], 'schedule'> {
  schedule: Schedule | ClassSchedule;
  status?: ClassStatus;
  primary_teacher?: Teacher;
  secondary_teacher?: Teacher;
}

export enum GradeLevel {
  PreK = "pre_k",
  PreSchool = "pre_school",
  Kindergarten = "kindergarten",
  First = "first",
  Second = "second",
  Third = "third",
  Fourth = "fourth",
  Fifth = "fifth",
  Sixth = "sixth",
  Seventh = "seventh",
  Eighth = "eighth",
  Ninth = "ninth",
  Tenth = "tenth",
  Eleventh = "eleventh",
  Twelfth = "twelfth"
}

export const GradeLevelLabels: Record<GradeLevel, string> = {
  [GradeLevel.PreK]: "Pre-K",
  [GradeLevel.PreSchool]: "Pre-School",
  [GradeLevel.Kindergarten]: "Kindergarten",
  [GradeLevel.First]: "1st Grade",
  [GradeLevel.Second]: "2nd Grade",
  [GradeLevel.Third]: "3rd Grade",
  [GradeLevel.Fourth]: "4th Grade",
  [GradeLevel.Fifth]: "5th Grade",
  [GradeLevel.Sixth]: "6th Grade",
  [GradeLevel.Seventh]: "7th Grade",
  [GradeLevel.Eighth]: "8th Grade",
  [GradeLevel.Ninth]: "9th Grade",
  [GradeLevel.Tenth]: "10th Grade",
  [GradeLevel.Eleventh]: "11th Grade",
  [GradeLevel.Twelfth]: "12th Grade"
};

export enum ClassStatus {
  Active = "active",
  Pending = "pending",
  Completed = "completed",
  Cancelled = "cancelled",
  Future = "future",
  Past = "past",
  Current = "current"
}

export const ClassStatusLabels: Record<ClassStatus, string> = {
  [ClassStatus.Active]: "Active",
  [ClassStatus.Pending]: "Pending",
  [ClassStatus.Completed]: "Completed",
  [ClassStatus.Cancelled]: "Cancelled",
  [ClassStatus.Future]: "Future",
  [ClassStatus.Past]: "Past",
  [ClassStatus.Current]: "Current"
};

export enum RelationshipType {
  Parent = "parent",
  Guardian = "guardian",
  Other = "other"
}

export const RelationshipTypeLabels: Record<RelationshipType, string> = {
  [RelationshipType.Parent]: "Parent",
  [RelationshipType.Guardian]: "Guardian",
  [RelationshipType.Other]: "Other"
};

export interface ParentGuardian {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  student_count?: number;
  relationship?: RelationshipType;
}

export interface PostgresChangePayload<T> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: T;
  old: T;
}

export interface AttendanceRecord {
  id: string;
  class_id: string;
  student_id: string;
  date: string;
  record_type: RecordType;
  status: AttendanceStatus;
  time: string;
  guardian_name: string | null;
  guardian_signature_url: string | null;
  notes: string | null;
  student?: Student;
}

export type AttendanceRecordWithStudent = AttendanceRecord & {
  student: Student;
}; 